import { DoubleSide, ShaderMaterial } from 'three';
import type { MapDef } from '@cloudpearl/config';
import { ATMOSPHERE, LIGHTING, NOISE, SHADOW_FRAG_PARS, SHADOW_VERT_MAIN, SHADOW_VERT_PARS, WORLD_UNIFORMS } from '../shaders/chunks';
import type { SharedUniforms } from '../shaders/shared';
import { col, litMaterial } from './common';
import type { MaterialTextures } from './common';

/**
 * Billboard leaf clumps: each card is a quad expanded in view space around its centre, so a
 * canopy of ~1–2k small cards reads as dense, fluffy foliage from every angle. Normals are
 * spherical from the clump centre (soft volumetric shading) and cards carry baked AO (deeper =
 * darker). The depth material billboards toward the light, giving dappled canopy shadows.
 */
const FOLIAGE_BODY = /* glsl */ `
attribute vec2 aCorner;
attribute vec4 aCard; // size, rotation, rand, ao
varying vec2 vUv;
varying float vAO;
varying float vRand;
varying float vTreeRand;
vec4 foliagePosition(out vec3 wN) {
  mat4 m = modelMatrix;
  #ifdef USE_INSTANCING
    m = modelMatrix * instanceMatrix;
  #endif
  vec4 center = m * vec4(position, 1.0);
  float scl = length(m[0].xyz);
  wN = normalize(mat3(m) * normal);
  float treeRand = hash12(m[3].xz * 0.173);
  float t = uTime;
  float phase = dot(center.xz, vec2(0.05, 0.07)) + treeRand * 6.28;
  float h = max(center.y - m[3].y, 0.0);
  // Canopies breathe gently, then heave and shiver when a pocket of air passes through.
  float pocket = gustWave(m[3].xz);
  float sway = (sin(t * 1.3 + phase) * 0.35 + 0.25) * uWind.z * 0.6 + pocket * (1.2 + 0.25 * sin(t * 7.0 + phase * 3.0));
  center.xz += uWind.xy * sway * 0.03 * h;
  // Leaf flutter, stronger at the canopy surface.
  center.xyz += wN * sin(t * 4.2 + aCard.z * 40.0) * 0.035 * scl * aCard.w * (0.4 + pocket * 1.8);
  vec3 camRight = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
  vec3 camUp = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
  float ang = aCard.y + sin(t * 2.0 + aCard.z * 20.0) * 0.08;
  float cs = cos(ang);
  float sn = sin(ang);
  vec2 corner = vec2(aCorner.x * cs - aCorner.y * sn, aCorner.x * sn + aCorner.y * cs);
  float size = aCard.x * scl;
  vUv = aCorner * 0.5 + 0.5;
  vAO = aCard.w;
  vRand = aCard.z;
  vTreeRand = treeRand;
  return vec4(center.xyz + (camRight * corner.x + camUp * corner.y) * size, 1.0);
}
`;

const FOLIAGE_VS = /* glsl */ `
${SHADOW_VERT_PARS}
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
${FOLIAGE_BODY}
varying vec3 vWorldPos;
varying vec3 vNormal;
void main() {
  vec3 wNormal;
  vec4 wPos = foliagePosition(wNormal);
  vWorldPos = wPos.xyz;
  vNormal = wNormal;
  gl_Position = projectionMatrix * viewMatrix * wPos;
  ${SHADOW_VERT_MAIN}
}
`;

const FOLIAGE_FS = /* glsl */ `
${SHADOW_FRAG_PARS}
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
${LIGHTING}
uniform sampler2D tLeaf;
uniform vec3 uLeafA;
uniform vec3 uLeafB;
uniform vec3 uLeafC;
uniform float uHueShift;
varying vec2 vUv;
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vAO;
varying float vRand;
varying float vTreeRand;
void main() {
  vec4 tex = texture2D(tLeaf, vUv);
  #ifdef A2C
    float alpha = smoothstep(0.35, 0.65, tex.a);
    if (alpha < 0.02) discard;
  #else
    if (tex.a < 0.5) discard;
    float alpha = 1.0;
  #endif
  vec3 V = normalize(cameraPosition - vWorldPos);
  // "Fluffy tree" shading: colour comes from a smooth volumetric gradient — the direction from
  // the canopy centre to this leaf against the sun — not from the card's own facing. The whole
  // crown reads as one soft, lumpy volume: deep shade on the far side, a lit body, a warm
  // highlight cap toward the sun.
  vec3 n = normalize(vNormal);
  float align = dot(n, uSunDir);
  float shadow = getShadowMask() * cloudShade(vWorldPos);
  float depthT = vAO * vAO;
  float leafV = tex.r;
  float ramp = smoothstep(-1.1, 1.1, align * 0.85 + (depthT - 0.55) * 0.55 + (leafV - 0.5) * 0.16 + (vRand - 0.5) * 0.08);
  vec3 albedo = mix(uLeafA, uLeafB, ramp);
  float hi = smoothstep(0.2, 1.4, align + depthT * 0.35 + (leafV - 0.5) * 0.3);
  albedo = mix(albedo, uLeafC, hi * 0.45);
  albedo *= 0.9 + vTreeRand * 0.2;
  albedo = mix(albedo, albedo * vec3(1.08, 1.0, 0.8), (tex.g - 0.5) * uHueShift);
  float sunTerm = smoothstep(-0.35, 1.0, align) * mix(0.4, 1.0, shadow);
  vec3 c = albedo * (hemiAmbient(n) * mix(0.55, 1.0, depthT) + uSunColor * sunTerm * 0.6);
  // Backlit: sunlight glows through the outer leaves.
  float back = pow(clamp(dot(-V, uSunDir), 0.0, 1.0), 3.0);
  c += uLeafC * uSunColor * back * 0.3 * shadow * depthT * (0.5 + leafV * 0.5);
  gl_FragColor = vec4(applyFog(c, vWorldPos), alpha);
}
`;

const FOLIAGE_DEPTH_VS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
${FOLIAGE_BODY}
void main() {
  vec3 wN;
  vec4 wPos = foliagePosition(wN);
  gl_Position = projectionMatrix * viewMatrix * wPos;
}
`;

const FOLIAGE_DEPTH_FS = /* glsl */ `
uniform sampler2D tLeaf;
varying vec2 vUv;
void main() {
  if (texture2D(tLeaf, vUv).a < 0.5) discard;
  gl_FragColor = vec4(1.0);
}
`;

export function createFoliageMaterials(shared: SharedUniforms, map: MapDef, tex: MaterialTextures, a2c: boolean): { material: ShaderMaterial; depth: ShaderMaterial } {
  const p = map.palette;
  const material = litMaterial(
    shared,
    { tLeaf: { value: tex.leaf }, uLeafA: col(p.leafA), uLeafB: col(p.leafB), uLeafC: col(p.leafC), uHueShift: { value: 0.35 } },
    { name: 'foliage', vertexShader: FOLIAGE_VS, fragmentShader: FOLIAGE_FS, defines: a2c ? { A2C: 1 } : {}, side: DoubleSide },
  );
  material.alphaToCoverage = a2c;
  const depth = new ShaderMaterial({
    name: 'foliage-depth',
    uniforms: { ...shared.all(), tLeaf: { value: tex.leaf } },
    vertexShader: FOLIAGE_DEPTH_VS,
    fragmentShader: FOLIAGE_DEPTH_FS,
    side: DoubleSide,
  });
  return { material, depth };
}
